import { FormControl, FormGroup } from '@angular/forms';
import { FormGroupRawValue } from '../../helpers/types.utils';
import {
  createDeliveryStatusField,
  createSearchTextField,
  createMessageTypeField,
  createStateCodesField,
  createMessageStatusesField,
  createPageNumberField,
  createPageSizeField
} from '../fields/broadcast-message-fields';
import {
  MessageTypeEnum,
  MessageStatusEnum,
  DeliveryStatusEnum
} from '../../enums';

/**
 * Interface for Broadcast Message search DTO (matches API)
 */
export interface BroadcastMessageSearchDtoForm {
  searchText?: string;
  type?: MessageTypeEnum;
  stateCodes?: string[];
  statuses?: MessageStatusEnum[];
  deliveryStatus?: DeliveryStatusEnum;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Type definition for the BroadcastMessageSearch FormGroup structure
 */
export type BroadcastMessageSearchFormGroup = FormGroup<{
  deliveryStatus: ReturnType<typeof createDeliveryStatusField>;
  searchText: ReturnType<typeof createSearchTextField>;
  type: FormControl<MessageTypeEnum | null>;
  stateCodes: ReturnType<typeof createStateCodesField>;
  statuses: ReturnType<typeof createMessageStatusesField>;
  pageNumber: ReturnType<typeof createPageNumberField>;
  pageSize: ReturnType<typeof createPageSizeField>;
}>;

/**
 * Interface for Broadcast Message search form value
 */
export type BroadcastMessageSearchFormValue =
  FormGroupRawValue<BroadcastMessageSearchFormGroup>;

/**
 * Creates a complete FormGroup for Broadcast Message search/filtering
 *
 * @param initial - Optional initial values for form fields
 * @returns FormGroup with all search/filter fields
 *
 * @example
 * ```typescript
 * const form = createBroadcastMessageSearchForm({
 *   deliveryStatus: DeliveryStatusEnum.Sent,
 *   searchText: null,
 *   type: null,
 *   stateCodes: [],
 *   statuses: [],
 *   pageNumber: 1,
 *   pageSize: 10
 * });
 * ```
 */
export function createBroadcastMessageSearchForm(
  initial?: Partial<BroadcastMessageSearchFormValue>
): BroadcastMessageSearchFormGroup {
  return new FormGroup({
    deliveryStatus: createDeliveryStatusField(initial?.deliveryStatus),
    searchText: createSearchTextField(initial?.searchText),
    type: createMessageTypeField({ required: false }, initial?.type),
    stateCodes: createStateCodesField(initial?.stateCodes),
    statuses: createMessageStatusesField(initial?.statuses),
    pageNumber: createPageNumberField(initial?.pageNumber),
    pageSize: createPageSizeField(initial?.pageSize)
  });
}

/**
 * Converts form value to BroadcastMessageSearchDtoForm for API call
 */
export function formValueToSearchDto(
  formValue: BroadcastMessageSearchFormValue
): BroadcastMessageSearchDtoForm {
  return {
    searchText: formValue.searchText || undefined,
    type: formValue.type || undefined,
    stateCodes:
      formValue.stateCodes && formValue.stateCodes.length > 0
        ? formValue.stateCodes
        : undefined,
    statuses:
      formValue.statuses && formValue.statuses.length > 0
        ? formValue.statuses
        : undefined,
    deliveryStatus: formValue.deliveryStatus,
    pageNumber: formValue.pageNumber,
    pageSize: formValue.pageSize
  };
}

/**
 * Creates initial form value from search DTO
 */
export function searchDtoToFormValue(
  dto: Partial<BroadcastMessageSearchDtoForm>
): Partial<BroadcastMessageSearchFormValue> {
  return {
    deliveryStatus: dto.deliveryStatus ?? DeliveryStatusEnum.Sent,
    searchText: dto.searchText ?? null,
    type: dto.type ?? null,
    stateCodes: dto.stateCodes ?? [],
    statuses: dto.statuses ?? [],
    pageNumber: dto.pageNumber ?? 1,
    pageSize: dto.pageSize ?? 10
  };
}

/**
 * Resets the form to initial values, optionally keeping the delivery status
 */
export function resetSearchForm(
  form: BroadcastMessageSearchFormGroup,
  keepDeliveryStatus = true
): void {
  const currentDeliveryStatus = form.controls.deliveryStatus.value;

  form.reset({
    deliveryStatus: keepDeliveryStatus
      ? currentDeliveryStatus
      : DeliveryStatusEnum.Sent,
    searchText: null,
    type: null,
    stateCodes: [],
    statuses: [],
    pageNumber: 1,
    pageSize: 10
  });
}
